import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  FileText, Upload, TrendingUp, TrendingDown, RefreshCw,
  Eye, Trash2, Lock, AlertTriangle, CheckCircle, X, Check
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';

const fmt = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n || 0);

function StatusBadge({ status }) {
  const map = {
    processing: { label: 'Processing', cls: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400', spin: true },
    parsing:    { label: 'Parsing',    cls: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400', spin: true },
    complete:   { label: 'Complete',   cls: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400', spin: false },
    error:      { label: 'Error',      cls: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400', spin: false },
    password_required: { label: 'Password Required', cls: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400', spin: false },
  };
  const { label, cls, spin } = map[status] || { label: status, cls: 'bg-slate-100 text-slate-600', spin: false };
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${cls}`}>
      {spin && <span className="w-2 h-2 border border-current border-t-transparent rounded-full animate-spin" />}
      {!spin && status === 'complete' && <CheckCircle className="w-3 h-3" />}
      {!spin && status === 'error' && <AlertTriangle className="w-3 h-3" />}
      {!spin && status === 'password_required' && <Lock className="w-3 h-3" />}
      {label}
    </span>
  );
}

function PasswordModal({ statementId, onClose, onUnlocked }) {
  const [pwd, setPwd] = useState('');
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!pwd) return;
    setLoading(true);
    try {
      await api.post(`/statements/${statementId}/unlock`, { password: pwd });
      toast.success('Password accepted. Processing started.');
      onUnlocked();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Wrong password. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open title="Unlock Password-Protected PDF" onClose={onClose}
      description="Enter the password set by your bank to unlock this statement.">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <input
            type={show ? 'text' : 'password'}
            placeholder="Bank PDF password"
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            autoFocus
            className="w-full h-9 px-3 pr-10 rounded-md border text-sm bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-600 text-slate-900 dark:text-slate-100 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-sky-500"
          />
          <button type="button" onClick={() => setShow(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 text-xs">
            {show ? 'Hide' : 'Show'}
          </button>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose} type="button">Cancel</Button>
          <Button type="submit" loading={loading}>{loading ? 'Unlocking…' : 'Unlock & Process'}</Button>
        </div>
      </form>
    </Dialog>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unlockTarget, setUnlockTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const pollRef = useRef(null);

  const firstName = user?.user_metadata?.full_name?.split(' ')[0] || user?.email?.split('@')[0] || 'User';

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const fetchStatements = useCallback(async () => {
    try {
      const r = await api.get('/statements');
      setStatements(r.data.statements || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatements();
  }, [fetchStatements]);

  // Poll if any statement is processing/parsing
  useEffect(() => {
    const needsPoll = statements.some(s => ['processing', 'parsing'].includes(s.status));
    if (needsPoll) {
      pollRef.current = setInterval(fetchStatements, 5000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [statements, fetchStatements]);

  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/statements/${deleteTarget}`);
      setStatements(s => s.filter(x => x.id !== deleteTarget));
      toast.success('Statement deleted');
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  // Summary stats
  const totalStatements = statements.filter(s => s.status === 'complete').length;
  const totalTxns = statements.reduce((a, s) => a + (s.transaction_count || 0), 0);
  const totalCredits = statements.reduce((a, s) => a + (s.total_credits || 0), 0);
  const totalDebits = statements.reduce((a, s) => a + (s.total_debits || 0), 0);

  const summaryCards = [
    { label: 'Processed Statements', value: totalStatements, icon: FileText, color: 'text-sky-600', bg: 'bg-sky-50 dark:bg-sky-900/20' },
    { label: 'Total Transactions', value: totalTxns.toLocaleString('en-IN'), icon: RefreshCw, color: 'text-violet-600', bg: 'bg-violet-50 dark:bg-violet-900/20' },
    { label: 'Total Credits', value: fmt(totalCredits), icon: TrendingUp, color: 'text-emerald-600', bg: 'bg-emerald-50 dark:bg-emerald-900/20' },
    { label: 'Total Debits', value: fmt(totalDebits), icon: TrendingDown, color: 'text-red-600', bg: 'bg-red-50 dark:bg-red-900/20' },
  ];

  return (
    <DashboardLayout>
      <div className="w-full min-w-0 max-w-full">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">{greeting}, {firstName} 👋</h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">Here's your statement overview</p>
        </div>
        <Button onClick={() => navigate('/upload')} className="gap-2">
          <Upload className="w-4 h-4" /> Upload PDF
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {summaryCards.map(({ label, value, icon: Icon, color, bg }) => (
          <Card key={label}>
            <CardContent className="pt-5">
              <div className={`w-10 h-10 rounded-lg ${bg} flex items-center justify-center mb-3`}>
                <Icon className={`w-5 h-5 ${color}`} />
              </div>
              <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">{value}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Statements Table */}
      <Card>
        <div className="px-6 py-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
          <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">Bank Statements</h2>
          <button
            onClick={fetchStatements}
            className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-500 transition-colors"
            title="Refresh"
          >
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : statements.length === 0 ? (
          <div className="p-12 text-center">
            <FileText className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-4" />
            <p className="text-slate-500 dark:text-slate-400 font-medium">No statements yet</p>
            <p className="text-slate-400 dark:text-slate-500 text-sm mt-1 mb-6">Upload your first bank statement PDF to get started.</p>
            <Button onClick={() => navigate('/upload')} className="gap-2">
              <Upload className="w-4 h-4" /> Upload Statement
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  {['Upload Date', 'Bank', 'Account', 'Period', 'Transactions', 'Status', 'Actions'].map(h => (
                    <th key={h} className="text-left px-6 py-3 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {statements.map((s, i) => (
                  <tr
                    key={s.id}
                    className={`border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors ${i % 2 === 0 ? '' : 'bg-slate-50/50 dark:bg-slate-900/50'}`}
                  >
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-400 whitespace-nowrap">
                      {new Date(s.upload_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                    <td className="px-6 py-4 font-medium text-slate-900 dark:text-slate-100">{s.bank_name || '—'}</td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-400 font-mono text-xs">{s.account_number || '—'}</td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-400 text-xs">{s.statement_period || '—'}</td>
                    <td className="px-6 py-4 text-slate-900 dark:text-slate-100 font-medium">
                      {s.transaction_count ? s.transaction_count.toLocaleString('en-IN') : '—'}
                    </td>
                    <td className="px-6 py-4"><StatusBadge status={s.status} /></td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        {s.status === 'password_required' && (
                          <button
                            onClick={() => setUnlockTarget(s.id)}
                            title="Unlock PDF"
                            className="p-1.5 rounded-lg hover:bg-amber-50 dark:hover:bg-amber-900/20 text-amber-600 transition-colors"
                          >
                            <Lock className="w-4 h-4" />
                          </button>
                        )}
                        {(s.status === 'complete' || s.status === 'error') && (
                          <button
                            onClick={() => navigate(`/review/${s.id}`)}
                            title="Review"
                            className="p-1.5 rounded-lg hover:bg-sky-50 dark:hover:bg-sky-900/20 text-sky-600 transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        )}
                        {deleteTarget === s.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={handleDelete}
                              disabled={deleting}
                              className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
                            >
                              <Check className="w-3 h-3" />
                              {deleting ? '…' : 'Delete'}
                            </button>
                            <button
                              onClick={() => setDeleteTarget(null)}
                              className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-300 transition-colors"
                            >
                              <X className="w-3 h-3" />
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setDeleteTarget(s.id)}
                            title="Delete"
                            className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Unlock modal */}
      {unlockTarget && (
        <PasswordModal
          statementId={unlockTarget}
          onClose={() => setUnlockTarget(null)}
          onUnlocked={fetchStatements}
        />
      )}
      </div>{/* end w-full min-w-0 wrapper */}
    </DashboardLayout>
  );
}
